/**
 * This file deals with saving data state (appState, elements, images, ...)
 * locally to the browser.
 *
 * Notes:
 *
 * - DataState refers to full state of the app: appState, elements, images,
 *   though some state is saved separately (collab username, library) for one
 *   reason or another. We also save different data to different storage
 *   (localStorage, indexedDB).
 */

import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import {
  CANVAS_SEARCH_TAB,
  DEFAULT_SIDEBAR,
  debounce,
} from "@excalidraw/common";
import { clearElementsForLocalStorage } from "@excalidraw/element";
import {
  createStore,
  entries,
  del,
  getMany,
  set,
  setMany,
  get,
} from "idb-keyval";

import { appJotaiStore, atom } from "excalidraw-app/app-jotai";

import type { LibraryPersistedData } from "@excalidraw/excalidraw/data/library";
import type { ImportedDataState } from "@excalidraw/excalidraw/data/types";
import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";
import type { MaybePromise } from "@excalidraw/common/utility-types";

import { SAVE_TO_LOCAL_STORAGE_TIMEOUT, STORAGE_KEYS } from "../app_constants";

import { FileManager } from "./FileManager";
import { Locker } from "./Locker";
import { loadFilesFromBackend, saveFilesToBackend } from "./LocalFileStorage";
import { updateBrowserStateVersion } from "./tabSync";
import { importFromLocalStorage } from "./localStorage";

const filesStore = createStore("files-db", "files-store");

export const localStorageQuotaExceededAtom = atom(false);

class LocalFileManager extends FileManager {
  clearObsoleteFiles = async (opts: { currentFileIds: FileId[] }) => {
    await entries(filesStore).then((entries) => {
      for (const [id, imageData] of entries as [FileId, BinaryFileData][]) {
        // if image is unused (not on canvas) & is older than 1 day, delete it
        // from storage. We check `lastRetrieved` we care about the last time
        // the image was used (loaded on canvas), not when it was initially
        // created.
        if (
          (!imageData.lastRetrieved ||
            Date.now() - imageData.lastRetrieved > 24 * 3600 * 1000) &&
          !opts.currentFileIds.includes(id as FileId)
        ) {
          del(id, filesStore);
        }
      }
    });
  };
}

const saveDataStateToLocalStorage = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
) => {
  const localStorageQuotaExceeded = appJotaiStore.get(
    localStorageQuotaExceededAtom,
  );
  try {
    const _appState = clearAppStateForLocalStorage(appState);

    if (
      _appState.openSidebar?.name === DEFAULT_SIDEBAR.name &&
      _appState.openSidebar.tab === CANVAS_SEARCH_TAB
    ) {
      _appState.openSidebar = null;
    }

    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
      JSON.stringify(clearElementsForLocalStorage(elements)),
    );
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
      JSON.stringify(_appState),
    );
    updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
    if (localStorageQuotaExceeded) {
      appJotaiStore.set(localStorageQuotaExceededAtom, false);
    }
  } catch (error: any) {
    // Unable to access window.localStorage
    console.error(error);
    if (isQuotaExceededError(error) && !localStorageQuotaExceeded) {
      appJotaiStore.set(localStorageQuotaExceededAtom, true);
    }
  }
};

const isQuotaExceededError = (error: any) => {
  return error instanceof DOMException && error.name === "QuotaExceededError";
};

type SavingLockTypes = "collaboration";

export class LocalData {
  private static _save = debounce(
    async (
      elements: readonly ExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
      onFilesSaved: () => void,
    ) => {
      saveDataStateToLocalStorage(elements, appState);

      await this.fileStorage.saveFiles({
        elements,
        files,
      });
      onFilesSaved();
    },
    SAVE_TO_LOCAL_STORAGE_TIMEOUT,
  );

  /** Saves DataState, including files. Bails if saving is paused */
  static save = (
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
    onFilesSaved: () => void,
  ) => {
    // we need to make the `isSavePaused` check synchronously (undebounced)
    if (!this.isSavePaused()) {
      this._save(elements, appState, files, onFilesSaved);
    }
  };

  static flushSave = () => {
    this._save.flush();
  };

  private static locker = new Locker<SavingLockTypes>();

  static pauseSave = (lockType: SavingLockTypes) => {
    this.locker.lock(lockType);
  };

  static resumeSave = (lockType: SavingLockTypes) => {
    this.locker.unlock(lockType);
  };

  static isSavePaused = () => {
    return document.hidden || this.locker.isLocked();
  };

  /**
   * Charger uniquement les préférences utilisateur (pas les éléments)
   * Utilisé pour les rooms de collaboration
   */
  static loadUserPreferences = async (): Promise<ImportedDataState | null> => {
    const data = importFromLocalStorage();
    if (!data) {
      return null;
    }
    return {
      appState: data.appState,
      elements: [],
      files: {},
    };
  };

  // ---------------------------------------------------------------------------

  static fileStorage = new LocalFileManager({
    getFiles(ids) {
      return getMany(ids, filesStore).then(
        async (filesData: (BinaryFileData | undefined)[]) => {
          const loadedFiles: BinaryFileData[] = [];
          const erroredFiles = new Map<FileId, true>();

          const filesToSave: [FileId, BinaryFileData][] = [];
          const missingIds: FileId[] = [];

          filesData.forEach((data, index) => {
            const id = ids[index];
            if (data) {
              const _data: BinaryFileData = {
                ...data,
                lastRetrieved: Date.now(),
              };
              filesToSave.push([id, _data]);
              loadedFiles.push(_data);
            } else {
              missingIds.push(id);
            }
          });

          try {
            // save loaded files back to storage with updated `lastRetrieved`
            setMany(filesToSave, filesStore);
          } catch (error) {
            console.warn(error);
          }

          if (missingIds.length) {
            const { loadedFiles: remoteFiles, erroredFiles: remoteErrored } =
              await loadFilesFromBackend(missingIds);

            if (remoteFiles.length) {
              try {
                setMany(
                  remoteFiles.map((file) => [file.id, file]),
                  filesStore,
                );
              } catch (error) {
                console.warn(error);
              }
              loadedFiles.push(...remoteFiles);
            }

            for (const [fileId] of remoteErrored) {
              erroredFiles.set(fileId, true);
            }
          }

          for (const id of missingIds) {
            if (!loadedFiles.some((file) => file.id === id)) {
              erroredFiles.set(id, true);
            }
          }

          return { loadedFiles, erroredFiles };
        },
      );
    },
    async saveFiles({ addedFiles }) {
      const savedFiles = new Map<FileId, BinaryFileData>();
      const erroredFiles = new Map<FileId, BinaryFileData>();

      // before we use `storage` event synchronization, let's update the flag
      // optimistically. Hopefully nothing fails, and an IDB read executed
      // before an IDB write finishes will read the latest value.
      updateBrowserStateVersion(STORAGE_KEYS.VERSION_FILES);

      await Promise.all(
        [...addedFiles].map(async ([id, fileData]) => {
          try {
            await set(id, fileData, filesStore);
          } catch (error: any) {
            console.error(error);
          }
        }),
      );

      const { savedFiles: remoteSaved, erroredFiles: remoteErrored } =
        await saveFilesToBackend({ files: addedFiles });

      for (const [fileId, fileData] of remoteSaved) {
        savedFiles.set(fileId, fileData);
      }
      for (const [fileId, fileData] of remoteErrored) {
        erroredFiles.set(fileId, fileData);
      }

      return { savedFiles, erroredFiles };
    },
  });
}
export class LibraryIndexedDBAdapter {
  /** IndexedDB database and store name */
  private static idb_name = STORAGE_KEYS.IDB_LIBRARY;
  /** library data store key */
  private static key = "libraryData";

  private static store = createStore(
    `${LibraryIndexedDBAdapter.idb_name}-db`,
    `${LibraryIndexedDBAdapter.idb_name}-store`,
  );

  static async load() {
    const IDBData = await get<LibraryPersistedData>(
      LibraryIndexedDBAdapter.key,
      LibraryIndexedDBAdapter.store,
    );

    if (!IDBData) {
      return { libraryItems: defaultLibraryItems };
    }

    return IDBData || null;
  }

  static save(data: LibraryPersistedData): MaybePromise<void> {
    return set(
      LibraryIndexedDBAdapter.key,
      data,
      LibraryIndexedDBAdapter.store,
    );
  }
}

// ---------------------------------------------------------------------------
// Cloud Adapter (Admin-UI)
// Persiste et charge la bibliothèque via un backend Admin-UI (hors conteneur).
// Utilise des variables d'environnement Vite pour la configuration.
// - VITE_ADMIN_LIBRARY_URL : base URL du backend (ex: https://api.admin-ui.example.com)
// - VITE_ADMIN_LIBRARY_TOKEN : token d'authentification (optionnel)
// Le backend doit exposer :
//   GET  {BASE}/library           => { libraryItems: LibraryItems }
//   PUT  {BASE}/library           body: { libraryItems: LibraryItems }
// En cas d'erreur ou d'absence de config, fallback local est appliqué.
export class AdminUILibraryAdapter {
  private static baseUrl: string | undefined = (import.meta as any).env
    ?.VITE_ADMIN_LIBRARY_URL;
  private static token: string | undefined = (import.meta as any).env
    ?.VITE_ADMIN_LIBRARY_TOKEN;

  private static url(path: string): string {
    const base = this.baseUrl?.replace(/\/$/, "");
    if (!base) {
      return path;
    }
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    if (typeof window !== "undefined") {
      return new URL(`${base}${normalizedPath}`, window.location.origin).toString();
    }
    return `${base}${normalizedPath}`;
  }

  private static headers(overrideToken?: string): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    const token = overrideToken || this.token;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  static async load(_metadata: { source: "load" | "save" }) {
    try {
      if (!this.baseUrl) {
        return { libraryItems: [] };
      }

      // Go directly to the templates library endpoint. Using the legacy
      // `/library` alias triggers a relative redirect that gets swallowed by
      // the canvas SPA route under the reverse proxy.
      const url = new URL(this.url("/templates/library"));

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
        credentials: "include",
      });
      if (!res.ok) {
        return { libraryItems: [] };
      }
      const data = await res.json();
      if (data?.libraryItems && Array.isArray(data.libraryItems)) {
        return { libraryItems: data.libraryItems };
      }
      return { libraryItems: [] };
    } catch (err) {
      console.warn("AdminUILibraryAdapter.load error", err);
      return { libraryItems: [] };
    }
  }

  static async save(libraryData: LibraryPersistedData) {
    void libraryData;
    return;
  }

  static async loadTemplate(id: string, token?: string) {
    if (!this.baseUrl) {
      return null;
    }
    try {
      const res = await fetch(this.url(`/templates/${id}`), {
        method: "GET",
        headers: this.headers(token),
      });
      if (!res.ok) {
        return null;
      }
      return await res.json();
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  static async saveTemplate(id: string, content: string, token?: string) {
    if (!this.baseUrl) {
      return;
    }
    try {
      await fetch(this.url(`/templates/${id}`), {
        method: "PUT",
        headers: this.headers(token),
        body: JSON.stringify({ content }),
      });
    } catch (e) {
      console.error(e);
    }
  }

  static async createTemplate(
    data: {
      name: string;
      content: string;
      description?: string;
      category?: string;
      language?: string;
      tags?: string[];
    },
    token?: string,
  ) {
    if (!this.baseUrl) {
      return null;
    }
    try {
      const res = await fetch(this.url("/templates"), {
        method: "POST",
        headers: this.headers(token),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        return null;
      }
      return await res.json();
    } catch (e) {
      console.error(e);
      return null;
    }
  }
}

export class LibraryLocalStorageMigrationAdapter {
  static load() {
    return null;
  }
  static clear() {
    return;
  }
}
